import { useState, useEffect } from "react";
import { getAuth, onAuthStateChanged, signInWithEmailAndPassword } from "firebase/auth";
import { collection, query, where, getDocs } from "firebase/firestore";
import { db } from "../firebase";
import axios from "axios";
import QRCode from "qrcode";
const auth = getAuth();
const API  = "http://localhost:5000/api/loyalty";

const THEME = {
  light: {
    bg: "#f8fafc", card: "#ffffff", border: "#e2e8f0",
    text1: "#0f172a", text2: "#64748b", text3: "#94a3b8",
    input: "#f8fafc", inputBorder: "#e2e8f0",
    btn: "#0f172a", btnText: "#ffffff",
    pillBg: "#eff6ff", pillText: "#1d4ed8",
    hintBg: "#fffbeb", hintBorder: "#fde68a", hintText: "#92400e",
    detailsBg: "#f8fafc",
    errorBg: "#fef2f2", errorBorder: "#fecaca", errorText: "#dc2626",
    stampFilled: "#0f172a", stampEmpty: "#e2e8f0",
    progressBg: "#f1f5f9",
  },
  dark: {
    bg: "#0f172a", card: "#1e293b", border: "#273549",
    text1: "#f1f5f9", text2: "#94a3b8", text3: "#475569",
    input: "#1a2740", inputBorder: "#273549",
    btn: "#378ADD", btnText: "#ffffff",
    pillBg: "#1e3a5f", pillText: "#85B7EB",
    hintBg: "#2a1e07", hintBorder: "#78350f", hintText: "#fcd34d",
    detailsBg: "#162032",
    errorBg: "#2d1515", errorBorder: "#7f1d1d", errorText: "#f87171",
    stampFilled: "#378ADD", stampEmpty: "#273549",
    progressBg: "#162032",
  },
};

export default function LoyaltyClaimPage() {
  const [state, setState]     = useState("loading");
  const [user, setUser]       = useState(null);
  const [contact, setContact] = useState(null);
  const [claimData, setClaimData] = useState(null);
  const [errorMsg, setErrorMsg]   = useState("");
  const [dark, setDark] = useState(() => localStorage.getItem("ws-theme") === "dark");

  const t = dark ? THEME.dark : THEME.light;
  const toggleTheme = () => {
    setDark(d => { localStorage.setItem("ws-theme", !d ? "dark" : "light"); return !d; });
  };

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u) => {
      if (!u) { setState("sign-in"); return; }
      setUser(u);
      await loadContact(u);
    });
    return unsub;
  }, []);

async function loadContact(u) {
  setState("loading");
  try {
    const snap = await getDocs(query(collection(db, "contact"), where("email", "==", u.email)));
    if (snap.empty) { setState("not-a-member"); return; }
    const d = { _docId: snap.docs[0].id, ...snap.docs[0].data() };
    setContact(d);

    // ── Si déjà une carte → aller directement chercher les stamps ──
    if (d.LoyaltyUUID && d.LoyaltyUUID !== "") {
      try {
        const res = await axios.post(`${API}/claim`, {
          firebaseId: u.uid,
          first_name: d.Name     || "",
          last_name:  d.Lastname || "",
          email:      d.email,
          phone:      d.Phone ? String(d.Phone) : undefined,
        });
        setClaimData(res.data);
        setState("card");
      } catch (err) {
        setErrorMsg("Erreur de chargement de votre carte.");
        setState("error");
      }
      return;
    }

    // ── Pas encore de carte → page de confirmation ──
    setState("confirm");
  } catch (err) {
    setErrorMsg("Erreur de chargement."); setState("error");
  }
}

  async function handleSignIn(email, password) {
    setState("loading");
    try {
      await signInWithEmailAndPassword(auth, email, password);
    } catch (err) {
      const msg =
        ["auth/invalid-credential","auth/user-not-found","auth/wrong-password"].includes(err.code)
          ? "E-mail ou mot de passe incorrect."
          : err.code === "auth/too-many-requests"
          ? "Trop de tentatives. Attendez quelques minutes."
          : "Connexion échouée.";
      setErrorMsg(msg); setState("sign-in-error");
    }
  }

  async function handleClaim() {
    setState("submitting");
    try {
      const res = await axios.post(`${API}/claim`, {
        firebaseId: user.uid,
        first_name: contact.Name     || "",
        last_name:  contact.Lastname || "",
        email:      contact.email,
        phone:      contact.Phone ? String(contact.Phone) : undefined,
      });
      setClaimData(res.data);
      setState("card");
    } catch (err) {
      const detail = err.response?.data;
      if (detail?.error === "not_a_member") { setState("not-a-member"); return; }
      setErrorMsg(detail?.details || detail?.error || "Une erreur est survenue.");
      setState("error");
    }
  }

  return (
    <div style={{ minHeight: "100vh", background: t.bg, display: "flex", alignItems: "center", justifyContent: "center", padding: "24px 16px", fontFamily: "'DM Sans','Segoe UI',sans-serif", transition: "background 0.2s" }}>

      {/* Theme toggle */}
      <div style={{ position: "fixed", top: 16, right: 16, zIndex: 10 }}>
        <button onClick={toggleTheme} style={{ width: 36, height: 20, borderRadius: 20, cursor: "pointer", position: "relative", background: t.detailsBg, border: `1px solid ${t.border}` }}>
          <div style={{ width: 14, height: 14, borderRadius: "50%", background: t.btn, position: "absolute", top: 2, left: 2, transition: "transform 0.2s", transform: dark ? "translateX(16px)" : "translateX(0)" }} />
        </button>
      </div>

      <div style={{ background: t.card, borderRadius: 20, padding: "40px 36px", maxWidth: 460, width: "100%", boxShadow: "0 4px 40px rgba(0,0,0,0.08)", border: `1px solid ${t.border}` }}>
        <Logo t={t} />
        {state === "loading"        && <Spinner t={t} label="Chargement…" />}
        {state === "submitting"     && <Spinner t={t} label="Génération de votre carte…" />}
        {state === "sign-in"        && <SignInView onSignIn={handleSignIn} errorMsg="" t={t} />}
        {state === "sign-in-error"  && <SignInView onSignIn={handleSignIn} errorMsg={errorMsg} t={t} />}
        {state === "not-a-member"   && <NotAMemberView t={t} />}
        {state === "confirm"        && <ConfirmView contact={contact} onClaim={handleClaim} t={t} />}
        {state === "card"           && <CardView data={claimData} t={t} dark={dark} />}
        {state === "error"          && <ErrorView message={errorMsg} onRetry={() => loadContact(user)} t={t} />}
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

// ── Sub-components ────────────────────────────────────────

function Logo({ t }) {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 32 }}>
      <img src="/logo.PNG" alt="WeStudy" style={{ height: 32, objectFit: "contain" }} onError={e => e.target.style.display = "none"} />
      <span style={{ fontSize: 11, color: t.text3, letterSpacing: "0.06em" }}>CARTE DE FIDÉLITÉ</span>
    </div>
  );
}

function Spinner({ t, label }) {
  return (
    <div style={{ textAlign: "center", padding: "8px 0" }}>
      <div style={{ width: 32, height: 32, border: `3px solid ${t.border}`, borderTop: `3px solid ${t.btn}`, borderRadius: "50%", animation: "spin 0.8s linear infinite", margin: "0 auto 16px" }} />
      <p style={{ fontSize: 13, color: t.text3, margin: 0 }}>{label}</p>
    </div>
  );
}

function SignInView({ onSignIn, errorMsg, t }) {
  const [email, setEmail]       = useState("");
  const [password, setPassword] = useState("");
  const inputStyle = { width: "100%", padding: "12px 14px", border: `1.5px solid ${t.inputBorder}`, borderRadius: 10, fontSize: 15, color: t.text1, outline: "none", boxSizing: "border-box", background: t.input };
  return (
    <div>
      <span style={{ display: "inline-block", background: t.pillBg, color: t.pillText, fontSize: 12, fontWeight: 600, padding: "4px 12px", borderRadius: 100, marginBottom: 16 }}>Carte de fidélité WeStudy</span>
      <h2 style={{ fontSize: 22, fontWeight: 700, color: t.text1, margin: "0 0 12px", letterSpacing: "-0.4px" }}>Connectez-vous pour récupérer votre carte</h2>
      <p style={{ fontSize: 15, color: t.text2, lineHeight: 1.6, margin: "0 0 20px" }}>Utilisez vos identifiants WeStudy habituels.</p>
      <div style={{ marginBottom: 16 }}>
        <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: t.text2, marginBottom: 6 }}>Adresse e-mail</label>
        <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="vous@exemple.com" style={inputStyle} />
      </div>
      <div style={{ marginBottom: 16 }}>
        <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: t.text2, marginBottom: 6 }}>Mot de passe</label>
        <input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="••••••••" style={inputStyle}
          onKeyDown={e => e.key === "Enter" && onSignIn(email, password)} />
      </div>
      {errorMsg && <p style={{ fontSize: 13, color: t.errorText, background: t.errorBg, border: `1px solid ${t.errorBorder}`, borderRadius: 8, padding: "10px 14px", margin: "0 0 16px" }}>{errorMsg}</p>}
      <button style={{ width: "100%", padding: 14, background: t.btn, color: t.btnText, border: "none", borderRadius: 12, fontSize: 15, fontWeight: 600, cursor: "pointer", opacity: (!email || !password) ? 0.5 : 1 }}
        onClick={() => onSignIn(email, password)} disabled={!email || !password}>
        Se connecter →
      </button>
      <p style={{ fontSize: 13, color: t.text3, margin: "16px 0 0", textAlign: "center" }}>
        Pas encore membre ?{" "}
        <a href="mailto:contact@westudy.tn" style={{ color: t.text1, fontWeight: 600, textDecoration: "underline" }}>Contactez-nous</a>
      </p>
    </div>
  );
}

function NotAMemberView({ t }) {
  return (
    <div style={{ textAlign: "center", padding: "8px 0" }}>
      <div style={{ width: 64, height: 64, borderRadius: "50%", background: t.hintBg, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px", fontSize: 28 }}>🔒</div>
      <h2 style={{ fontSize: 22, fontWeight: 700, color: t.text1, margin: "0 0 12px" }}>Compte WeStudy introuvable</h2>
      <p style={{ fontSize: 15, color: t.text2, lineHeight: 1.6, margin: "0 0 16px" }}>Cette carte est réservée aux membres existants.</p>
      <a href="mailto:contact@westudy.tn" style={{ color: t.btn, fontWeight: 600, textDecoration: "underline", fontSize: 14 }}>Contactez-nous</a>
    </div>
  );
}

function ConfirmView({ contact, onClaim, t }) {
  return (
    <div>
      <span style={{ display: "inline-block", background: t.pillBg, color: t.pillText, fontSize: 12, fontWeight: 600, padding: "4px 12px", borderRadius: 100, marginBottom: 16 }}>Votre carte de fidélité</span>
      <h2 style={{ fontSize: 22, fontWeight: 700, color: t.text1, margin: "0 0 12px", letterSpacing: "-0.4px" }}>Récupérez votre carte gratuite</h2>
      <p style={{ fontSize: 15, color: t.text2, lineHeight: 1.6, margin: "0 0 20px" }}>Collectez des tampons à chaque achat et débloquez des récompenses exclusives.</p>
      <div style={{ background: t.detailsBg, borderRadius: 12, marginBottom: 16, border: `1px solid ${t.border}` }}>
        <DetailRow label="Nom"    value={`${contact?.Name || ""} ${contact?.Lastname || ""}`.trim()} t={t} />
        <DetailRow label="E-mail" value={contact?.email} t={t} last={!contact?.Phone} />
        {contact?.Phone && <DetailRow label="Téléphone" value={String(contact.Phone)} t={t} last />}
      </div>
      <p style={{ fontSize: 13, color: t.hintText, lineHeight: 1.5, margin: "0 0 24px", padding: "12px 14px", background: t.hintBg, borderRadius: 8, border: `1px solid ${t.hintBorder}` }}>
        💡 Votre carte sera générée instantanément — ajoutez-la à Apple ou Google Wallet en un tap.
      </p>
      <button style={{ width: "100%", padding: 14, background: t.btn, color: t.btnText, border: "none", borderRadius: 12, fontSize: 15, fontWeight: 600, cursor: "pointer" }} onClick={onClaim}>
        Générer ma carte →
      </button>
    </div>
  );
}

function DetailRow({ label, value, t, last }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 16px", borderBottom: last ? "none" : `1px solid ${t.border}` }}>
      <span style={{ fontSize: 13, color: t.text3, fontWeight: 500 }}>{label}</span>
      <span style={{ fontSize: 14, color: t.text1, fontWeight: 600 }}>{value}</span>
    </div>
  );
}

function CardView({ data, t, dark }) {
  const stamps         = data?.stamps          || 0;
  const stampsRequired = data?.stamps_required || 10;
  const appleUrl       = data?.apple_wallet_url;
  const googleUrl      = data?.google_wallet_url;
  const isNew          = !data?.already_claimed;
  const cardUuid       = data?.card_uuid;
  const customerId     = data?.customer_id;

  const [qrDataUrl, setQrDataUrl] = useState(null);

  useEffect(() => {
    const qrValue = cardUuid || String(customerId) || "";
    if (!qrValue) return;
    QRCode.toDataURL(qrValue, {
      width:  200,
      margin: 2,
      color: {
        dark:  dark ? "#f1f5f9" : "#0f172a",
        light: dark ? "#1e293b" : "#ffffff",
      },
    }).then(setQrDataUrl).catch(console.error);
  }, [cardUuid, customerId, dark]);

  return (
    <div>
      {/* Header */}
      <div style={{ textAlign: "center", marginBottom: 24 }}>
        <div style={{ width: 64, height: 64, borderRadius: "50%", background: isNew ? "#f0fdf4" : t.pillBg, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px", fontSize: 28 }}>
          {isNew ? "🎉" : "🏷️"}
        </div>
        <h2 style={{ fontSize: 22, fontWeight: 700, color: t.text1, margin: "0 0 8px", letterSpacing: "-0.4px" }}>
          {isNew ? "Votre carte est prête !" : "Votre carte de fidélité"}
        </h2>
        <p style={{ fontSize: 14, color: t.text2, margin: 0, lineHeight: 1.6 }}>
          {isNew ? "Bienvenue dans le programme de fidélité WeStudy." : "Retrouvez votre progression ci-dessous."}
        </p>
      </div>

      {/* QR Code */}
      {qrDataUrl && (
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", marginBottom: 20 }}>
          <div style={{ background: dark ? "#1e293b" : "#ffffff", border: `1px solid ${t.border}`, borderRadius: 16, padding: 16, display: "inline-block" }}>
            <img src={qrDataUrl} alt="QR Code carte fidélité" style={{ width: 160, height: 160, display: "block" }} />
          </div>
          <p style={{ fontSize: 11, color: t.text3, marginTop: 8 }}>
            Présentez ce QR code en caisse
          </p>
        </div>
      )}

      {/* Stamp progress */}
      <div style={{ background: dark ? "#1a2740" : "#f8fafc", border: `1px solid ${t.border}`, borderRadius: 16, padding: 20, marginBottom: 16 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: t.text2 }}>Tampons collectés</span>
          <span style={{ fontSize: 18, fontWeight: 700, color: t.text1 }}>{stamps} / {stampsRequired}</span>
        </div>

        {/* Progress bar */}
        <div style={{ height: 8, background: t.stampEmpty, borderRadius: 8, overflow: "hidden", marginBottom: 16 }}>
          <div style={{ height: "100%", width: `${Math.min(100, (stamps / stampsRequired) * 100)}%`, background: t.btn, borderRadius: 8, transition: "width 0.6s ease" }} />
        </div>

        {/* Stamp grid */}
        <div style={{ display: "grid", gridTemplateColumns: `repeat(${Math.min(stampsRequired, 5)}, 1fr)`, gap: 8 }}>
          {Array.from({ length: stampsRequired }).map((_, i) => (
            <div key={i} style={{
              aspectRatio: "1", borderRadius: 10,
              background: i < stamps ? t.stampFilled : t.stampEmpty,
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: i < stamps ? 16 : 12,
              transition: "background 0.3s",
              border: i < stamps ? "none" : `1.5px dashed ${t.border}`,
            }}>
              {i < stamps ? "✦" : ""}
            </div>
          ))}
        </div>

        {stamps >= stampsRequired && (
          <div style={{ marginTop: 14, padding: "10px 14px", background: "#f0fdf4", borderRadius: 10, textAlign: "center", fontSize: 13, fontWeight: 600, color: "#16a34a" }}>
            🎁 Carte complète — une récompense vous attend !
          </div>
        )}
      </div>

      {/* Wallet buttons */}
      {(appleUrl || googleUrl) && (
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 10, marginBottom: 20 }}>
          <p style={{ fontSize: 13, color: t.text2, margin: "0 0 4px", textAlign: "center" }}>
            Ajoutez votre carte à votre wallet :
          </p>
          {appleUrl && (
            <a href={appleUrl} target="_blank" rel="noreferrer">
              <img src="https://upload.wikimedia.org/wikipedia/commons/thumb/3/30/Add_to_Apple_Wallet_badge.svg/3840px-Add_to_Apple_Wallet_badge.svg.png"
                alt="Add to Apple Wallet" style={{ height: 48, width: "auto", display: "block" }} />
            </a>
          )}
          {googleUrl && (
            <a href={googleUrl} target="_blank" rel="noreferrer">
              <img src="https://upload.wikimedia.org/wikipedia/commons/thumb/b/bb/Add_to_Google_Wallet_badge.svg/1280px-Add_to_Google_Wallet_badge.svg.png"
                alt="Add to Google Wallet" style={{ height: 48, width: "auto", display: "block" }} />
            </a>
          )}
        </div>
      )}

      <p style={{ fontSize: 13, color: t.text3, textAlign: "center", margin: 0 }}>
        Besoin d'aide ?{" "}
        <a href="mailto:contact@westudy.tn" style={{ color: t.btn, fontWeight: 600, textDecoration: "underline" }}>contact@westudy.tn</a>
      </p>
    </div>
  );
}

function ErrorView({ message, onRetry, t }) {
  return (
    <div style={{ textAlign: "center", padding: "8px 0" }}>
      <div style={{ width: 64, height: 64, borderRadius: "50%", background: t.errorBg, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px", fontSize: 28 }}>⚠️</div>
      <h2 style={{ fontSize: 22, fontWeight: 700, color: t.text1, margin: "0 0 12px" }}>Une erreur est survenue</h2>
      <p style={{ fontSize: 15, color: t.text2, lineHeight: 1.6, margin: "0 0 24px" }}>{message}</p>
      <button style={{ width: "100%", padding: 14, background: t.text2, color: "#fff", border: "none", borderRadius: 12, fontSize: 15, fontWeight: 600, cursor: "pointer" }} onClick={onRetry}>
        Réessayer
      </button>
    </div>
  );
}